/* Rum and Gin ranges: a two-position switch between them, and as each arrives a full-panel
   splash reading OUR RUM or OUR GIN holds for a moment, then fades to reveal the bottles.
   It plays again each time you come back to that section. */
(() => {
  'use strict';
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!document.getElementById('range') || !document.getElementById('gin')) return;   // only where both ranges exist
  if (document.documentElement.classList.contains('shopify-design-mode') || window.Shopify?.designMode) return;   // never in the theme editor
  const SECTIONS = [['range', 'Rum'], ['gin', 'Gin']];
  const io = new IntersectionObserver(entries => entries.forEach(e => {
    const splash = e.target.querySelector(':scope > .splash');
    if (e.intersectionRatio >= 0.6 && !splash.classList.contains('play')) splash.classList.add('play');
    else if (e.intersectionRatio < 0.05) splash.classList.remove('play');   // gone: reset for next time
  }), { threshold: [0, 0.05, 0.6] });

  SECTIONS.forEach(([id, word]) => {
    const sec = document.getElementById(id);
    if (!sec) return;
    const s = document.createElement('div');
    s.className = 'splash';
    s.setAttribute('aria-hidden', 'true');
    s.innerHTML = `<span><small>Our</small>${word}</span>`;
    sec.appendChild(s);
    io.observe(sec);
  });

  /* Rum and Gin act as a two-position switch you drag. Sitting on one, start swiping towards the
     other and its OUR RUM / OUR GIN panel fades up over the page, the word growing with your drag.
     Let go past about a third of the way and it commits: the page slides to the other range and
     the panel fades away there. Let go early and it springs back. A mouse-wheel click (one big
     step) commits straight away. Swiping up off Rum or down off Gin scrolls the page normally, and
     a scroll that stops part-way into the pair glides onto the nearer one. */
  const secs = SECTIONS.map(([id]) => document.getElementById(id)).filter(Boolean);
  const headerHeight = () => document.querySelector('.top')?.getBoundingClientRect().height || 60;
  const FLIP_MS = 700, HOLD_MS = 450, COMMIT = 0.35, FOLLOW = 0.25;
  const ease = t => t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  const tops = () => secs.map(s => Math.round(s.getBoundingClientRect().top + scrollY - headerHeight()));
  // Font swaps, fractional pixels and Safari's address bar can move the initial anchor.
  const at = () => {   // the section we're resting on; forgiving while the page is still settling
    const t = tops(), tol = Math.max(48, innerHeight * 0.18);
    let best = -1;
    t.forEach((v, k) => { if (Math.abs(v - scrollY) <= tol && (best < 0 || Math.abs(v - scrollY) < Math.abs(t[best] - scrollY))) best = k; });
    return best;
  };
  const span = () => innerHeight * 0.32;                                // drag distance for a full peek
  let anim = 0, lockUntil = 0, idle;

  // the peek panel: a fixed copy of the other range's splash, driven by --p (0 hidden, 1 full)
  const peek = document.createElement('div');
  peek.className = 'splash peek';
  peek.setAttribute('aria-hidden', 'true');
  peek.innerHTML = '<span><small>Our</small><b></b></span>';
  document.body.appendChild(peek);
  let p = 0, pAnim = 0;
  const setP = v => { p = Math.max(0, Math.min(1, v)); peek.style.setProperty('--p', p); };
  const tweenP = (to, ms) => {
    cancelAnimationFrame(pAnim);
    const from = p, t0 = performance.now();
    const step = now => { const t = Math.min(1, (now - t0) / ms); setP(from + (to - from) * ease(t)); pAnim = t < 1 ? requestAnimationFrame(step) : 0; };
    pAnim = requestAnimationFrame(step);
  };
  const dress = j => {   // colour and word of the range we're heading to
    const bg = j === 0 ? getComputedStyle(document.documentElement).getPropertyValue('--hl')
                       : getComputedStyle(secs[1]).getPropertyValue('--gbg');
    peek.style.setProperty('--sp-bg', bg.trim());
    peek.querySelector('b').textContent = SECTIONS[j][1];
  };

  let settling = false;   // true while a settle glide runs: the user's next scroll may interrupt it
  const glide = (to, dur, done, hold = HOLD_MS) => {
    cancelAnimationFrame(anim);
    const from = scrollY, t0 = performance.now();
    lockUntil = t0 + dur + hold;
    const step = now => {
      const t = Math.min(1, (now - t0) / dur);
      const target = typeof to === 'function' ? to() : to;
      scrollTo({ top: from + (target - from) * ease(t), behavior: 'instant' });
      if (t < 1) anim = requestAnimationFrame(step); else { anim = 0; settling = false; done?.(); }
    };
    anim = requestAnimationFrame(step);
  };
  const locked = () => anim || performance.now() < lockUntil;
  const interrupt = () => { if (settling) { cancelAnimationFrame(anim); anim = 0; lockUntil = 0; settling = false; } };
  let engaged = false;   // no settling until the visitor has scrolled themselves
  const engage = () => { engaged = true; };

  // gesture state: origin section, its scroll position, and which way we're heading
  let g = null;
  // the section whose span we're in (from just above its top to just above the next one's)
  const within = () => { const t = tops(); let k = -1; t.forEach((v, n) => { if (scrollY >= v - 48) k = n; }); return k; };
  const begin = dir => {
    let i = at();
    // part-way down into Rum and heading for Gin: that still counts as leaving Rum
    if (i < 0 && dir > 0) { const k = within(); if (k >= 0 && scrollY < tops()[k] + innerHeight * 0.9) i = k; }
    const j = i + dir;
    if (i < 0 || j < 0 || j >= secs.length) return false;
    // off a section by more than a nudge on the far side (e.g. still above Rum, scrolling down):
    // let the page scroll and settle onto it, rather than flipping past it
    const off = scrollY - tops()[i];
    if (Math.abs(off) > 48 && off * dir < 0) return false;
    g = { i, j, dir, y0: scrollY };
    dress(j);
    return true;
  };
  const drag = d => {   // d: distance dragged towards the target, in px
    if (!g) return;
    setP(d / span());
    scrollTo({ top: g.y0 + g.dir * Math.max(0, d) * FOLLOW, behavior: 'instant' });
  };
  const release = () => {
    if (!g) return;
    const { i, j } = g; g = null;
    if (p >= COMMIT) {
      tweenP(1, 160);
      glide(() => tops()[j], FLIP_MS, () => {
        const sp = secs[j].querySelector(':scope > .splash');
        sp.classList.remove('play'); void sp.offsetWidth; sp.classList.add('play');   // restart its fade from full
        cancelAnimationFrame(pAnim); setP(0);                                        // hand over to it seamlessly
      });
    } else {
      tweenP(0, 260);
      glide(() => tops()[i], 320);
    }
  };

  const blocked = () => document.body.classList.contains('locked');

  // touch: follow the finger. While a finger is down we never settle; the finger is in charge.
  // modes: 'flip' drags the peek towards the other range; 'manual' moves the page with the finger
  // (after a flip gesture reverses towards somewhere there's nothing to flip to); 'free' is native scroll.
  let sx = 0, sy = 0, mode = null, touching = false, my0 = 0, mdir = 0;
  addEventListener('touchstart', e => { engage(); interrupt(); touching = true; sx = e.touches[0].clientX; sy = e.touches[0].clientY; mode = null; }, { passive: true });
  addEventListener('touchmove', e => {
    if (blocked() || !e.touches.length) return;
    if (locked() && !g && mode !== 'manual') { e.preventDefault(); return; }
    const y = e.touches[0].clientY;
    const dx = sx - e.touches[0].clientX, dy = sy - y;   // dy > 0: finger up = towards Gin
    if (mode === null && Math.hypot(dx, dy) > 8) {
      if (Math.abs(dy) <= Math.abs(dx)) mode = 'free';
      else if (begin(Math.sign(dy))) mode = 'flip';
      // resting on Rum or Gin but heading where there's nothing to flip to (up into the hero, down
      // past Gin): move the page with the finger ourselves, so turning back can still flip
      else if (at() >= 0) { mode = 'manual'; my0 = scrollY; mdir = Math.sign(dy); }
      else mode = 'free';
    }
    if (mode === 'manual') {
      e.preventDefault();
      const md = sy - y;
      if (Math.sign(md) === -mdir && Math.abs(md) > 24) {   // turned back past the start
        scrollTo({ top: my0, behavior: 'instant' });
        if (begin(-mdir)) { mode = 'flip'; sy = y; return; }
      }
      scrollTo({ top: my0 + md, behavior: 'instant' });
      return;
    }
    if (mode !== 'flip') return;
    e.preventDefault();
    const d = dy * g.dir;
    if (d < -24) {   // the finger has turned back past where it started: head the other way instead
      const back = -g.dir, y0 = g.y0;
      cancelAnimationFrame(pAnim); setP(0); g = null;
      scrollTo({ top: y0, behavior: 'instant' });
      sy = y;
      if (!begin(back)) { mode = 'manual'; my0 = y0; }
      return;
    }
    drag(d);
  }, { passive: false });
  const endTouch = () => {
    touching = false;
    if (mode === 'flip') release();
    else queueSettle();   // finger lifted after a free or manual drag: now settle onto the nearer range
    mode = null;
  };
  addEventListener('touchend', endTouch, { passive: true });
  addEventListener('touchcancel', endTouch, { passive: true });

  // wheel: trackpads stream small deltas, so accumulate them like a drag and release when they stop;
  // a mouse-wheel click is one big step, so commit it outright
  let acc = 0, wheelIdle;
  addEventListener('wheel', e => {
    if (blocked()) return;
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
    engage(); interrupt();
    if (locked() && !g) { e.preventDefault(); return; }
    if (!g) { if (Math.abs(e.deltaY) < 1 || !begin(Math.sign(e.deltaY))) return; acc = 0; }
    e.preventDefault();
    const big = e.deltaMode !== 0 || Math.abs(e.deltaY) >= 60;
    acc += e.deltaY * g.dir;
    if (big) { setP(1); release(); return; }
    drag(acc);
    clearTimeout(wheelIdle);
    wheelIdle = setTimeout(release, 140);
  }, { passive: false });

  addEventListener('keydown', e => {
    if (e.target.closest('input, textarea, select, [contenteditable], .gin-track, dialog, .book, .sheet')) return;
    const dir = { ArrowDown: 1, PageDown: 1, ' ': e.shiftKey ? -1 : 1, ArrowUp: -1, PageUp: -1 }[e.key];
    if (!dir) return;
    engage(); interrupt();
    if (locked()) { e.preventDefault(); return; }
    if (begin(dir)) { e.preventDefault(); setP(1); release(); }
  });

  // Settle on entry and after late layout changes, including a fresh uncached load.
  // Retry after animation locks; otherwise an early scroll could miss its only settle.
  const settle = () => {
    clearTimeout(idle);
    if (blocked() || g || !engaged || touching) return;
    if (locked()) {
      idle = setTimeout(settle, Math.max(50, lockUntil - performance.now() + 20));
      return;
    }
    const t = tops();
    if (scrollY < t[0] - innerHeight * 0.35 || scrollY > t.at(-1) + innerHeight * 0.35) return;
    const i = t.reduce((best, value, index) => Math.abs(value - scrollY) < Math.abs(t[best] - scrollY) ? index : best, 0);
    if (Math.abs(t[i] - scrollY) > 1) { settling = true; glide(() => tops()[i], 600, null, 0); }
  };
  // fonts/images arriving late shift the sections: if we're resting on one, stay on it — instantly
  const realign = () => {
    if (anim || g || blocked()) return;
    const i = at();
    if (i < 0) return;
    const top = tops()[i];
    if (Math.abs(top - scrollY) > 1) scrollTo({ top, behavior: 'instant' });
  };
  const queueSettle = () => {
    clearTimeout(idle);
    if (!anim && !g) idle = setTimeout(settle, 140);
  };
  addEventListener('scroll', queueSettle, { passive: true });
  addEventListener('pageshow', realign);
  addEventListener('load', realign, { once: true });
  addEventListener('resize', realign);
  document.fonts?.ready.then(realign);
  const layout = new ResizeObserver(realign);
  secs.forEach(sec => layout.observe(sec));
  const intro = document.querySelector('.open');
  if (intro) layout.observe(intro);
})();
